import type { ReactNode } from 'react';
import { formatMoney } from './utils';

/** Overwerkgrens per 4-weken periode (CAO art 23). */
export const OVERWERK_GRENS = 152;

type Soorten = Record<'gewerkt' | 'ziek' | 'verlof', { uren: number; bruto: number }>;
type Row = [ReactNode, ReactNode] | false;

const SOORT_LABEL = { gewerkt: 'Gewerkt', ziek: 'Ziek', verlof: 'Verlof' } as const;
const SOORTEN = ['gewerkt', 'ziek', 'verlof'] as const;

/** Uren van de hele periode per soort + overwerk. Zelfde rijen in overzicht en kalender. */
export function urenRows(s: Soorten | undefined, hele: number, fmtUren: (n: number) => string): Row[] {
  return [
    ...SOORTEN.map((k): Row => !!s && (k === 'gewerkt' || s[k].uren > 0) && [SOORT_LABEL[k], fmtUren(s[k].uren)]),
    ['Overwerk', hele > OVERWERK_GRENS ? fmtUren(hele - OVERWERK_GRENS) : `vanaf ${OVERWERK_GRENS}u`],
  ];
}

/**
 * Staat het rooster van deze periode vol, en kan er nog een dienst bij? De uren t.o.v. de
 * overwerkgrens beslissen; de weekdekking zegt of de planner de periode al heeft uitgeroosterd
 * (zonder diensten is een lage urenstand geen ruimte maar een leeg rooster).
 */
export function roosterRows(
  rooster: { weken: number[]; laatste_dienst: string | null } | undefined,
  hele: number,
  fmtUren: (n: number) => string,
  fmtDatum: (d: string) => string
): Row[] {
  if (!rooster) return [];
  const leeg = rooster.weken.every((n) => n === 0);
  const gaten = rooster.weken.filter((n) => n === 0).length;
  const ruimte = Math.max(0, OVERWERK_GRENS - hele);
  return [
    [
      'Rooster',
      leeg ? 'Nog niet uitgeroosterd' : gaten === 0 ? 'Alle weken gevuld' : `${gaten} ${gaten === 1 ? 'week' : 'weken'} leeg, tot ${fmtDatum(rooster.laatste_dienst!)}`,
    ],
    !leeg && ['Past er nog een dienst bij?', ruimte > 0 ? `Ja, ${fmtUren(ruimte)} ruimte` : 'Nee, vol'],
  ];
}

type Post = { key: string; label: string; bruto: number; netto: number | null };

/**
 * Geldposten van de hele periode in de gekozen stand. Komt rechtstreeks uit de centrale
 * berekening (server), dus overzicht en kalender tonen altijd dezelfde bedragen.
 */
export function postenRows(posten: Post[] | undefined, modus: 'bruto' | 'netto', reiskosten = 0): Row[] {
  return [
    ...(posten ?? []).map((p): Row => {
      const v = modus === 'bruto' ? p.bruto : p.netto;
      return [p.label, v == null ? '–' : formatMoney(v)];
    }),
    // Reiskosten zijn onbelast: bruto = netto. Ze komen met het loon mee, dus staan ze in de lijst en in het totaal.
    reiskosten > 0 && ['Reiskosten', formatMoney(reiskosten)],
  ];
}

/** Periodetotaal in de gekozen stand, inclusief de onbelaste reiskosten (zelfde som als postenRows). */
export function periodeTotaal(totaal: { bruto: number; netto: number | null } | undefined, modus: 'bruto' | 'netto', reiskosten = 0): number | null {
  if (!totaal) return null;
  const v = modus === 'bruto' ? totaal.bruto : totaal.netto;
  return v == null ? null : Math.round((v + reiskosten) * 100) / 100;
}
